import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  validateMnemonic,
  mnemonicBytesToSeed,
  deriveGonkaPrivateKey,
  derivePublicKey,
  pubkeyToAddress,
  deriveKeyFromPin,
  encrypt,
  generateNonce,
  zeroize,
} from '@gonka-wallet/crypto';
import {
  openWalletDb,
  saveWallet,
  type EncryptedWalletBlob,
} from '@gonka-wallet/storage';
import { Button, Field } from '@gonka-wallet/ui-tokens';
import { PinPad } from '../components/PinPad.js';
import { BiometricOfferModal } from '../components/BiometricOfferModal.js';
import { isWebAuthnAvailable } from '../lib/webauthn.js';
import { useWalletStore } from '../stores/walletStore.js';
import { bytesToHex } from '../lib/format.js';

type Stage = 'mnemonic' | 'pin' | 'confirm-pin';

function ImportNav({ step, onBack, total = 3, t }: { step: number; onBack: () => void; total?: number; t: (key: string) => string }) {
  return (
    <>
      <div className="flex items-center justify-between py-2">
        <button type="button" onClick={onBack} className="text-link text-[15px]">
          ‹ {t('common.back')}
        </button>
        <div className="text-[16px] font-semibold text-text-primary">{t('import.title')}</div>
        <div className="text-[11px] text-text-tertiary">{step} of {total}</div>
      </div>
      <div className="flex justify-center gap-1.5">
        {Array.from({ length: total }, (_, i) => (
          <div
            key={i}
            className={`h-0.5 w-7 rounded-full ${
              i === step - 1 ? 'bg-text-primary' : 'bg-surface-3'
            }`}
          />
        ))}
      </div>
    </>
  );
}

export function Import() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const addWallet = useWalletStore((s) => s.addWallet);
  const wallets = useWalletStore((s) => s.wallets);
  const watched = useWalletStore((s) => s.watched);
  const removeWatched = useWalletStore((s) => s.removeWatched);
  const [mnemonic, setMnemonic] = useState('');
  const [walletName, setWalletName] = useState(() => t('import.defaultWalletName'));
  const nameEditedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>('mnemonic');
  const [firstPin, setFirstPin] = useState<string | null>(null);
  const [mnemonicFocused, setMnemonicFocused] = useState(false);
  const [pendingOffer, setPendingOffer] = useState<{
    wallet: EncryptedWalletBlob;
    derivedKey: Uint8Array;
  } | null>(null);

  const isValidMnemonic = mnemonic.trim().length > 0 && validateMnemonic(mnemonic);

  // If the imported phrase resolves to an address that is already in the
  // watched list, surface that saved nickname as the default wallet name
  // instead of "Imported wallet". Stop as soon as the user edits the field
  // manually.
  useEffect(() => {
    if (nameEditedRef.current) return;
    if (!isValidMnemonic) return;
    let cancelled = false;
    void (async () => {
      try {
        const plaintext = new TextEncoder().encode(mnemonic);
        const seed = await mnemonicBytesToSeed(plaintext);
        const priv = deriveGonkaPrivateKey(seed);
        const pub = derivePublicKey(priv);
        const addr = pubkeyToAddress(pub);
        zeroize(priv);
        zeroize(seed);
        zeroize(plaintext);
        if (cancelled || nameEditedRef.current) return;
        const match = watched.find((w) => w.address === addr);
        if (match) setWalletName(match.name);
      } catch {
        /* ignore — invalid phrase will fail later in validateAndAdvance */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mnemonic, isValidMnemonic, watched]);

  function stageIndex(): number {
    if (stage === 'mnemonic') return 1;
    if (stage === 'pin') return 2;
    return 3;
  }

  function handleBack() {
    if (stage === 'mnemonic') {
      navigate(-1);
    } else if (stage === 'pin') {
      setStage('mnemonic');
      setError(null);
    } else {
      setStage('pin');
      setFirstPin(null);
      setError(null);
    }
  }

  function validateAndAdvance() {
    if (!validateMnemonic(mnemonic)) {
      setError(t('import.invalidMnemonic'));
      return;
    }
    setError(null);
    setStage('pin');
  }

  function handlePinSet(pin: string) {
    setFirstPin(pin);
    setStage('confirm-pin');
  }

  async function handlePinConfirm(pin: string) {
    if (pin !== firstPin) {
      setStage('pin');
      setFirstPin(null);
      setError(null);
      return;
    }
    await finalize(pin);
  }

  async function finalize(pin: string) {
    let seed: Uint8Array = new Uint8Array(0);
    let priv: Uint8Array = new Uint8Array(0);
    let key: Uint8Array = new Uint8Array(0);
    let plaintext: Uint8Array = new Uint8Array(0);
    let keepKey = false;
    try {
      // Encode once and use byte-form seed derivation to minimize
      // JS-string residency of mnemonic (spec §4.3 I2).
      plaintext = new TextEncoder().encode(mnemonic);
      // Clear the React-state mnemonic string as early as possible so
      // it does not linger through the PIN stages.
      setMnemonic('');
      seed = await mnemonicBytesToSeed(plaintext);
      priv = deriveGonkaPrivateKey(seed);
      const pub = derivePublicKey(priv);
      const address = pubkeyToAddress(pub);

      const duplicate = wallets.some((w) => w.addresses.includes(address));
      if (duplicate) {
        setError(t('create.duplicateWallet'));
        return;
      }

      const salt = crypto.getRandomValues(new Uint8Array(16));
      key = await deriveKeyFromPin(pin, salt);
      const nonce = generateNonce();
      const ciphertext = encrypt(key, nonce, plaintext);

      const trimmedName = walletName.trim() || t('import.defaultWalletName');
      const blob: EncryptedWalletBlob = {
        version: 1,
        id: crypto.randomUUID(),
        name: trimmedName.slice(0, 64),
        kdf: 'argon2id',
        kdfParams: { m: 65536, t: 3, p: 4 },
        salt: bytesToHex(salt),
        nonce: bytesToHex(nonce),
        ciphertext: bytesToHex(ciphertext),
        hdPath: "m/44'/1200'/0'/0/0",
        addresses: [address],
        createdAt: new Date().toISOString(),
      };

      const db = await openWalletDb();
      await saveWallet(db, blob);
      addWallet(blob);

      // If this address was previously monitored as a watch-only entry,
      // drop that entry now that the user holds the signing phrase —
      // keeping both would surface the same wallet twice on /wallet.
      const matchingWatch = watched.find((w) => w.address === address);
      if (matchingWatch) removeWatched(matchingWatch.id);

      // Same biometric offer flow as Create — show before navigating
      // to /home so the user opts in immediately and never has to
      // type the PIN twice.
      if (isWebAuthnAvailable()) {
        const keyCopy = new Uint8Array(key);
        setPendingOffer({ wallet: blob, derivedKey: keyCopy });
        keepKey = true;
        return;
      }

      navigate('/home');
    } finally {
      if (keepKey) {
        zeroize(seed, priv, plaintext);
      } else {
        zeroize(seed, priv, key, plaintext);
      }
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-bg px-5 pb-6 pt-3 text-text-primary">
      <ImportNav step={stageIndex()} onBack={handleBack} t={t} />

      <div className={`mt-4 flex flex-1 flex-col ${stage !== 'mnemonic' ? 'items-center justify-center gap-6' : 'gap-3.5'}`}>
        {stage === 'mnemonic' && (
          <>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
                {t('import.recoveryPhrase')}
              </div>
              <div className="mt-1.5 text-[18px] font-semibold text-text-primary">
                {t('import.pasteOrType')}
              </div>
            </div>

            <Field
              label={t('import.mnemonicLabel')}
              hint={
                error ? (
                  <span className="text-neg">{error}</span>
                ) : mnemonic.trim() && isValidMnemonic ? (
                  <span className="text-pos">{t('import.validBip39')}</span>
                ) : undefined
              }
              focused={mnemonicFocused}
            >
              <textarea
                value={mnemonic}
                onChange={(e) => setMnemonic(e.target.value)}
                onFocus={() => setMnemonicFocused(true)}
                onBlur={() => setMnemonicFocused(false)}
                placeholder={t('import.mnemonicPlaceholder')}
                rows={6}
                className="w-full resize-none bg-transparent outline-none font-mono text-[13px] leading-relaxed text-text-primary"
              />
            </Field>

            <Field label={t('import.walletName')}>
              <input
                type="text"
                value={walletName}
                onChange={(e) => {
                  nameEditedRef.current = true;
                  setWalletName(e.target.value);
                }}
                placeholder={t('import.walletNamePlaceholder')}
                maxLength={64}
                className="w-full bg-transparent outline-none text-[14px] text-text-primary"
              />
            </Field>

            <div className="mt-auto">
              <Button
                variant="primary"
                fullWidth
                onClick={validateAndAdvance}
                disabled={!isValidMnemonic}
              >
                {t('import.continue')}
              </Button>
            </div>
          </>
        )}

        {(stage === 'pin' || stage === 'confirm-pin') && (
          <>
            <div className="text-center">
              <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
                {stage === 'pin'
                  ? t('create.pin')
                  : t('create.pinConfirm')}
              </div>
              <div className="mt-3 text-[22px] font-semibold tracking-[-0.4px] text-text-primary">
                {stage === 'pin' ? t('create.createPin') : t('create.enterAgain')}
              </div>
              {stage === 'pin' && (
                <div className="mt-3 text-[14px] leading-relaxed text-text-secondary">
                  {t('create.pinDescription')}
                </div>
              )}
            </div>

            <PinPad onComplete={stage === 'pin' ? handlePinSet : handlePinConfirm} />

            {error && (
              <div className="mt-2 text-center text-[11px] text-neg">{error}</div>
            )}
          </>
        )}
      </div>

      {pendingOffer && (
        <BiometricOfferModal
          wallet={pendingOffer.wallet}
          derivedKey={pendingOffer.derivedKey}
          onDone={() => {
            setPendingOffer(null);
            navigate('/home');
          }}
        />
      )}
    </div>
  );
}
